// Demo-only report generator. Produces CSVs whose columns match exactly the
// real backend (ui-scheduled-tasks/reports/templates/template_*.py).
// Tree-shaken from production builds because it is only ever require()'d
// inside a `REACT_APP_DEMO_MODE === 'true'` branch.

import { getAssistedConversions, getAssistedConversionImpressions, getDailyImpressions } from "./assistedConversions";
import { getCreatives } from "./creatives";
import { getCampaignSettings } from "./campaignSettings";
import { getDateRangeBounds } from "../../utils/dateUtils";

// ---------- CSV helpers ----------

function esc(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csv(headers, dataRows) {
  return [headers.map(esc).join(","), ...dataRows.map(r => r.map(esc).join(","))].join("\r\n");
}

function triggerDownload(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function inRange(isoStr, startDate, endDate) {
  if (!isoStr) return false;
  const t = new Date(isoStr).getTime();
  return t >= new Date(`${startDate}T00:00:00Z`).getTime() &&
         t <= new Date(`${endDate}T23:59:59Z`).getTime();
}

function fmtDate(isoStr) {
  return isoStr ? isoStr.slice(0, 10) : "";
}

function fmtCurrency(n) {
  if (n == null) return "—";
  return `$${Number(n).toFixed(2)}`;
}

function fmtPct(n) {
  if (n == null) return "—";
  return `${Number(n).toFixed(2)}%`;
}

function fmtRoas(n) {
  if (n == null) return "—";
  return `${Number(n).toFixed(2)}x`;
}

function fmtLift(n) {
  if (n == null) return "N/A";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(1)}%`;
}

function fmtLaunch(isoStr) {
  if (!isoStr) return "—";
  try {
    return new Date(isoStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return isoStr.slice(0, 10); }
}

function fmtMarket(row) {
  return [row.region, row.city, row.country].filter(Boolean).join(", ");
}

// ---------- Template 1 — Performance Summary ----------
// Columns: Date, Impressions, Conversions, Revenue, Spend
// Source: get_dashboard_chart_data RPC + assisted_conversions spend/revenue

function template1(startDate, endDate) {
  const dailyImps = getDailyImpressions().filter(d =>
    inRange(`${d.date}T12:00:00Z`, startDate, endDate),
  );
  const conversions = getAssistedConversions().filter(c =>
    inRange(c.created_at, startDate, endDate),
  );

  const dayMap = {};
  dailyImps.forEach(d => {
    dayMap[d.date] = { impressions: d.impressions, conversions: 0, revenue: 0, spend: 0 };
  });
  conversions.forEach(c => {
    const day = fmtDate(c.created_at);
    if (!dayMap[day]) dayMap[day] = { impressions: 0, conversions: 0, revenue: 0, spend: 0 };
    dayMap[day].conversions += 1;
    dayMap[day].revenue += c.event_value ?? 0;
    dayMap[day].spend += c.spend ?? 0;
  });

  const rows = Object.entries(dayMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, d]) => [
      day,
      d.impressions,
      d.conversions,
      Number(d.revenue.toFixed(2)),
      Number(d.spend.toFixed(2)),
    ]);

  return csv(["Date", "Impressions", "Conversions", "Revenue", "Spend"], rows);
}

// ---------- Template 2 — Conversion Detail Report ----------
// Columns: Order ID, Date, Customer, Type, Revenue, Podcast, Market, Creative
// Source: assisted_conversions + last impression per conversion

function template2(startDate, endDate) {
  const conversions = getAssistedConversions().filter(c =>
    inRange(c.created_at, startDate, endDate),
  );
  const impressions = getAssistedConversionImpressions();

  // Last impression per conversion (by created_at desc — take first match since
  // impressions are in build order; use a simple map keyed by conversion_id)
  const lastImpByConv = {};
  impressions.forEach(imp => {
    const cid = imp.assisted_conversion_id;
    if (!lastImpByConv[cid] || imp.created_at > lastImpByConv[cid].created_at) {
      lastImpByConv[cid] = imp;
    }
  });

  const rows = conversions.map(c => {
    const imp = lastImpByConv[c.id] || {};
    return [
      c.event_id || `AC-${c.id}`,
      fmtDate(c.created_at),
      c.visitor_id || "",
      c.event_type || "",
      c.event_value != null ? Number(c.event_value).toFixed(2) : "",
      imp.content_series_name || "",
      fmtMarket(c),
      imp.asset_name || "",
    ];
  });

  return csv(["Order ID", "Date", "Customer", "Type", "Revenue", "Podcast", "Market", "Creative"], rows);
}

// ---------- Template 3 — Podcast Performance Report ----------
// Columns: Rank, Podcast, Conversions, Spend, Conv. Rate
// Conv. Rate = conversions / total_impressions_for_that_podcast

function template3(startDate, endDate) {
  const conversions = getAssistedConversions().filter(c =>
    inRange(c.created_at, startDate, endDate),
  );
  const convById = Object.fromEntries(conversions.map(c => [c.id, c]));
  const impressions = getAssistedConversionImpressions().filter(
    imp => convById[imp.assisted_conversion_id],
  );

  // Aggregate per podcast
  const podMap = {};
  impressions.forEach(imp => {
    const conv = convById[imp.assisted_conversion_id];
    if (!conv) return;
    const podcast = imp.content_series_name || imp.publisher_name || imp.supply_partner_name || "";
    if (!podcast) return;
    if (!podMap[podcast]) podMap[podcast] = { conversions: 0, spend: 0, impressions: 0, seen: new Set() };
    podMap[podcast].impressions += 1; // each impression row = 1 impression served
    if (!podMap[podcast].seen.has(conv.id)) {
      podMap[podcast].seen.add(conv.id);
      podMap[podcast].conversions += 1;
      podMap[podcast].spend += conv.spend ?? 0;
    }
  });

  const sorted = Object.entries(podMap)
    .map(([podcast, d]) => ({
      podcast,
      conversions: d.conversions,
      spend: d.spend,
      convRate: d.impressions > 0 ? d.conversions / d.impressions : null,
    }))
    .sort((a, b) => b.conversions - a.conversions);

  const rows = sorted.map((p, i) => [
    i + 1,
    p.podcast,
    p.conversions,
    fmtCurrency(p.spend),
    fmtPct(p.convRate != null ? p.convRate * 100 : null),
  ]);

  return csv(["Rank", "Podcast", "Conversions", "Spend", "Conv. Rate"], rows);
}

// ---------- Template 5 — Monthly Budget Pacing Report ----------
// Columns: Month, Monthly Budget, Spend to Date, Budget Remaining,
//          Days Elapsed, Days Remaining, Daily Spend Rate,
//          Projected EOM Spend, Pacing Status, Pacing Delta

function template5(startDate, endDate) {
  const settings = getCampaignSettings();
  const conversions = getAssistedConversions();

  const today = new Date();
  const totalDays = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const daysElapsed = today.getDate();
  const daysRemaining = totalDays - daysElapsed;

  const rows = settings.map(s => {
    const monthYear = s.month_year?.slice(0, 7) || "";
    const monthLabel = monthYear
      ? new Date(`${monthYear}-15`).toLocaleDateString("en-US", { month: "long", year: "numeric" })
      : monthYear;

    const budget = s.spend_cap ? Number(s.spend_cap) : null;
    const actualSpend = conversions
      .filter(c => c.created_at && c.created_at.startsWith(monthYear))
      .reduce((sum, c) => sum + (c.spend ?? 0), 0);

    const dailyRate = daysElapsed > 0 ? actualSpend / daysElapsed : 0;
    const projectedEOM = dailyRate * totalDays;
    const budgetRemaining = budget != null ? budget - actualSpend : null;

    const isHistorical = endDate < today.toISOString().slice(0, 10);
    const hasBudget = budget != null && budget > 0;

    let pacingStatus = "—";
    let pacingDelta = null;
    if (hasBudget && !isHistorical) {
      const expectedToday = budget * (daysElapsed / totalDays);
      if (actualSpend > expectedToday * 1.10) pacingStatus = "Ahead";
      else if (actualSpend < expectedToday * 0.90) pacingStatus = "Behind";
      else pacingStatus = "On Track";
      pacingDelta = ((projectedEOM - budget) / budget) * 100;
    }

    const row = [
      monthLabel,
      fmtCurrency(budget),
      fmtCurrency(actualSpend),
      hasBudget ? fmtCurrency(budgetRemaining) : "—",
      daysElapsed,
      daysRemaining,
      fmtCurrency(dailyRate),
    ];

    if (isHistorical || !hasBudget) {
      row.push("—", "—", "—");
    } else {
      row.push(fmtCurrency(projectedEOM), pacingStatus, fmtLift(pacingDelta));
    }
    return row;
  });

  return csv(
    ["Month", "Monthly Budget", "Spend to Date", "Budget Remaining",
     "Days Elapsed", "Days Remaining", "Daily Spend Rate",
     "Projected EOM Spend", "Pacing Status", "Pacing Delta"],
    rows,
  );
}

// ---------- Template 6 — Creative Performance Comparison ----------
// Columns: Creative, Launched, Conversions, CVR%, Spend, Revenue, ROAS, Lift vs. Baseline
// CVR = conversions / impressions (impressions from assisted_conversion_impressions)

function template6(startDate, endDate) {
  const creatives = getCreatives();
  const impressions = getAssistedConversionImpressions();
  const conversions = getAssistedConversions().filter(c =>
    inRange(c.created_at, startDate, endDate),
  );
  const convById = Object.fromEntries(conversions.map(c => [c.id, c]));

  // Aggregate per creative_id
  const statsByCreative = {};
  impressions.forEach(imp => {
    const cid = imp.creative_id;
    if (!statsByCreative[cid]) statsByCreative[cid] = { conversions: 0, spend: 0, revenue: 0, impressions: 0, seen: new Set() };
    statsByCreative[cid].impressions += 1;
    const conv = convById[imp.assisted_conversion_id];
    if (conv && !statsByCreative[cid].seen.has(conv.id)) {
      statsByCreative[cid].seen.add(conv.id);
      statsByCreative[cid].conversions += 1;
      statsByCreative[cid].spend += conv.spend ?? 0;
      statsByCreative[cid].revenue += conv.event_value ?? 0;
    }
  });

  // Build per-creative results
  const result = creatives.map(c => {
    const stats = statsByCreative[c.id] || { conversions: 0, spend: 0, revenue: 0, impressions: 0 };
    const cvr = stats.impressions > 0 ? (stats.conversions / stats.impressions) * 100 : null;
    const roas = stats.spend > 0 ? stats.revenue / stats.spend : null;
    return { creative: c.name, launched: c.created_at, conversions: stats.conversions, cvr, spend: stats.spend, revenue: stats.revenue, roas };
  }).filter(r => r.conversions > 0).sort((a, b) => b.conversions - a.conversions);

  // Lift vs baseline (rank 1 creative)
  const baselineCvr = result[0]?.cvr ?? null;
  result.forEach((r, i) => {
    if (baselineCvr == null || baselineCvr === 0 || r.cvr == null) r.lift = null;
    else if (i === 0) r.lift = 0;
    else r.lift = ((r.cvr - baselineCvr) / baselineCvr) * 100;
  });

  const rows = result.map(r => [
    r.creative,
    fmtLaunch(r.launched),
    r.conversions,
    fmtPct(r.cvr),
    fmtCurrency(r.spend),
    fmtCurrency(r.revenue),
    fmtRoas(r.roas),
    fmtLift(r.lift),
  ]);

  return csv(["Creative", "Launched", "Conversions", "CVR%", "Spend", "Revenue", "ROAS", "Lift vs. Baseline"], rows);
}

// ---------- public ----------

const GENERATORS = { 1: template1, 2: template2, 3: template3, 5: template5, 6: template6 };
const FILE_NAMES = { 1: "performance-summary", 2: "conversion-detail", 3: "podcast-performance", 5: "budget-pacing", 6: "creative-performance" };

export function downloadDemoReport(template, dateRange, appliedCustomStart, appliedCustomEnd) {
  const { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
  const generator = GENERATORS[template.id];
  if (!generator) return false;
  const content = generator(startDate, endDate);
  const slug = FILE_NAMES[template.id] || `template-${template.id}`;
  triggerDownload(`apple-audio_${slug}_${startDate}_${endDate}.csv`, content);
  return true;
}
